<template>
  <div>
    <div v-if="content.historicalData && displayedProviders" class="text-center mt-8 md:mt-12">
      <h2 class="mb-2">
        <span class="block text-center title mb-2">{{
          $t('top_providers.title', { count: displayedProviders })
        }}</span>
        <span class="block subtitle1 font-normal">{{ subtitle }}</span>
      </h2>
      <p class="lead font-normal mb-5">
        {{
          $t('top_providers.subtitle', {
            formattedCount: formatNumber(content.historicalData.nbSearches, $lang),
            count: content.historicalData.nbSearches,
          })
        }}
      </p>
      <a class="link text-20 lg:text-24 block" href="#imt-search-modal">
        {{ $t('top_providers.compare_results') }}
        <svg role="presentation" focusable="false" tabindex="-1" class="icon icon-xs align-baseline -rotate-90"
           
          alt=""
          svg-inline
         xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 32 19">
  <path fill="currentColor" fill-rule="evenodd"
    d="M.8 1.18a2.26 2.26 0 0 1 3.2 0L16 13.2 28.03 1.18a2.26 2.26 0 0 1 3.2 3.2L17.6 17.97c-.88.9-2.31.9-3.2 0L.8 4.39a2.26 2.26 0 0 1 0-3.2Z"
    clip-rule="evenodd" />
</svg>
      </a>
      <template v-if="showTwoSections">
        <ProvidersSection
          section="bank"
          :title="$t('top_providers.best_for_bank')"
          :top-providers="topBank"
          :total-providers="content.historicalData.nbProvidersBank"
        />
        <ProvidersSection
          section="cash"
          :title="$t('top_providers.best_for_cash')"
          :top-providers="topCash"
          :total-providers="content.historicalData.nbProvidersCash"
        />
      </template>
      <ProvidersSection
        v-else
        section="overall"
        :top-providers="topOverall"
        :total-providers="content.historicalData.nbProviders"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { GQLTopProvidersSlice } from './generated/top-providers'
import ProvidersSection from './providers-section.vue'
import { getTopProviders } from './getTopProviders'
import { analytics } from '@core/analytics/core-events/lib'
import { View } from '@core/analytics/actions'
import { TopProviders as TopProviderObject } from '@core/analytics/objects/TopProviders'

const props = defineProps<{ content: GQLTopProvidersSlice }>()

const { $track, $lang, $t } = useNuxtApp()

onMounted(async () => {
  await $track(
    analytics(
      new View(),
      new TopProviderObject({
        topProviders: {
          nbOfSearches: props.content.historicalData?.nbSearches ?? 0,
          overallCheapest: props.content.historicalData?.cheapest?.provider.slug,
          overallBestRated: props.content.historicalData?.bestRated?.provider.slug,
          overallFastest: props.content.historicalData?.fastest?.provider.slug,
          bankCheapest: props.content.historicalData?.cheapestBank?.provider.slug,
          bankBestRated: props.content.historicalData?.bestRatedBank?.provider.slug,
          bankFastest: props.content.historicalData?.fastestBank?.provider.slug,
          cashCheapest: props.content.historicalData?.cheapestCash?.provider.slug,
          cashBestRated: props.content.historicalData?.bestRatedCash?.provider.slug,
          cashFastest: props.content.historicalData?.fastestCash?.provider.slug,
        },
      }),
    ),
  )
})

const subtitle = computed(() => {
  if (props.content.historicalData?.countryTo && !props.content.historicalData.countryFrom) {
    return $t('top_providers.for_transfers_to', {
      to: props.content.historicalData.countryTo.labels.to,
    })
  }
  if (props.content.historicalData?.countryTo && props.content.historicalData.countryFrom) {
    return $t('top_providers.for_transfers', {
      from: props.content.historicalData.countryFrom.labels.from,
      to: props.content.historicalData.countryTo.labels.to,
    })
  }
  return ''
})

const bankSearches = computed(() => {
  return props.content.historicalData?.nbSearchesBank ?? 0
})

const cashSearches = computed(() => {
  return props.content.historicalData?.nbSearchesCash ?? 0
})

const topOverall = computed(() => {
  if (props.content.historicalData === null) return []
  const { cheapest, fastest, bestRated } = props.content.historicalData
  return getTopProviders({
    cheapest,
    fastest,
    bestRated,
  })
})

const topBank = computed(() => {
  if (props.content.historicalData === null) return []
  const { cheapestBank, fastestBank, bestRatedBank } = props.content.historicalData
  return getTopProviders({
    cheapest: cheapestBank,
    fastest: fastestBank,
    bestRated: bestRatedBank,
  })
})

const topCash = computed(() => {
  if (props.content.historicalData === null) return []
  const { cheapestCash, fastestCash, bestRatedCash } = props.content.historicalData
  return getTopProviders({
    cheapest: cheapestCash,
    fastest: fastestCash,
    bestRated: bestRatedCash,
  })
})

const displayedProviders = computed(() => {
  if (showTwoSections.value) {
    return new Set([
      ...topBank.value.map(({ provider }) => provider.slug),
      ...topCash.value.map(({ provider }) => provider.slug),
    ]).size
  }
  return topOverall.value.length
})

const showTwoSections = computed(() => {
  const threshold = (props.content.historicalData?.nbSearches ?? 0) * 0.05
  return bankSearches.value > threshold && cashSearches.value > threshold
})
</script>
